import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification, updateNotification } from '@mantine/notifications';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { formsTemplatesService } from '@/services/formsTemplates.service';
import type { CreateFormTemplateTagPayload, UpdateFormTemplatePayload } from '@/types/formsTemplates';

const FT_TAG_CREATE_ID = 'ft-tag-create';
const FT_TAG_DELETE_ID = 'ft-tag-delete';
const FT_DELETE_ID = 'ft-delete';
const FT_STATUS_ID = 'ft-status';

export const useCreateFormTemplateTagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFormTemplateTagPayload) => formsTemplatesService.createFormTemplateTag(data),
    onMutate: () => {
      showNotification({
        id: FT_TAG_CREATE_ID,
        title: 'Creating tag',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATE_TAGS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATES] });
      updateNotification({
        id: FT_TAG_CREATE_ID,
        title: 'Success',
        message: 'Tag created successfully',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: FT_TAG_CREATE_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create tag',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};

export const useDeleteFormTemplateTagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formsTemplatesService.deleteFormTemplateTag(id),
    onMutate: () => {
      showNotification({
        id: FT_TAG_DELETE_ID,
        title: 'Deleting tag',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATE_TAGS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATES] });
      updateNotification({
        id: FT_TAG_DELETE_ID,
        title: 'Success',
        message: 'Tag deleted successfully',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: FT_TAG_DELETE_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete tag',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};

export const useDeleteFormTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formsTemplatesService.deleteFormTemplate(id),
    onMutate: () => {
      showNotification({
        id: FT_DELETE_ID,
        title: 'Deleting template',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATES] });
      updateNotification({
        id: FT_DELETE_ID,
        title: 'Success',
        message: 'Template deleted successfully',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: FT_DELETE_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete template',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};

export const useUpdateFormTemplateStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFormTemplatePayload }) =>
      formsTemplatesService.updateFormTemplate(id, payload),
    onMutate: () => {
      showNotification({
        id: FT_STATUS_ID,
        title: 'Updating template status',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FORM_TEMPLATE_BY_ID] });
      updateNotification({
        id: FT_STATUS_ID,
        title: 'Success',
        message: 'Template status updated',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: FT_STATUS_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update status',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};
